import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, MicOff, CheckCircle2, Truck, AlertCircle } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';
import DateTimePickerField from './DateTimePickerField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Trip request form embedded inside the chat widget.
 *
 * Two ways to fill it:
 *   1. Type each field manually.
 *   2. Tap the mic button, speak naturally ("I want to ship 2 horses from
 *      Casablanca to Marrakech on August 25th"), and the AI parses the
 *      transcript into the form fields.
 *
 * Voice transcription uses the browser-native Web Speech API (free,
 * instant, supports fr/en/es/nl). Parsing uses the backend's
 * /api/chat/parse-trip endpoint backed by Claude Sonnet 4.5.
 */
const TripFormInBot = ({ customer, language = 'en', onCreated, t }) => {
  // Browser Web Speech API support detection — done once.
  const SpeechRecognition = (typeof window !== 'undefined') && (
    window.SpeechRecognition || window.webkitSpeechRecognition
  );
  const voiceSupported = !!SpeechRecognition;

  const [fields, setFields] = useState({
    pickup_address: '',
    dropoff_address: '',
    transport_date: '',
    horse_count: '',
    additional_comments: '',
  });
  // Voice-first flow: start on the voice intro screen and only switch to the
  // form once the user has either spoken their request or chosen to type.
  const [step, setStep] = useState(voiceSupported ? 'voice-intro' : 'form');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(null); // {request_id} on success

  // Voice state
  const [listening, setListening] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceError, setVoiceError] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Cleanup speech recognition on unmount
    return () => {
      try { recognitionRef.current?.stop(); } catch {
        // ignore
      }
    };
  }, []);

  const langMap = { en: 'en-US', fr: 'fr-FR', es: 'es-ES', nl: 'nl-NL' };

  const updateField = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const startListening = () => {
    if (!voiceSupported || listening) return;
    setVoiceError('');
    setVoiceTranscript('');
    const recognition = new SpeechRecognition();
    recognition.lang = langMap[language] || 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;
    let finalTranscript = '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += text;
        else interim += text;
      }
      setVoiceTranscript((finalTranscript + interim).trim());
    };
    recognition.onerror = (e) => {
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
        setVoiceError(
          (t('chat.voice.permissionDenied') || 'Microphone permission denied.') +
          ' ' +
          (t('chat.voice.howToFix') ||
            "Click the 🔒 / 🎤 icon in your browser's address bar, set Microphone to 'Allow', then refresh this page.")
        );
      } else if (e?.error === 'no-speech') {
        setVoiceError(t('chat.voice.noSpeech') || "I didn't hear anything — try again and speak after the mic turns red.");
      } else if (e?.error === 'audio-capture') {
        setVoiceError(t('chat.voice.noMic') || 'No microphone detected on this device.');
      } else {
        setVoiceError(t('chat.voice.error') || 'Voice capture failed — please try again.');
      }
      setListening(false);
    };
    recognition.onend = async () => {
      setListening(false);
      const transcript = finalTranscript.trim();
      if (!transcript) return;
      // Parse transcript → structured fields via backend
      setParsing(true);
      try {
        const { data } = await axios.post(`${API}/chat/parse-trip`, {
          transcript,
          language,
        });
        // Additive merge — only overwrite fields the user spoke about.
        setFields(prev => ({
          pickup_address: data.pickup_address || prev.pickup_address,
          dropoff_address: data.dropoff_address || prev.dropoff_address,
          transport_date: data.transport_date || prev.transport_date,
          horse_count: data.horse_count || prev.horse_count,
          additional_comments: data.additional_comments || prev.additional_comments,
        }));
        // Voice-first UX: once we have something useful, drop the user
        // straight into the (now pre-filled) form so they can review + submit.
        setStep('form');
      } catch {
        setVoiceError(t('chat.voice.parseError') || "Couldn't parse what you said — please fill in manually.");
        setStep('form');
      } finally {
        setParsing(false);
      }
    };
    try {
      recognition.start();
      recognitionRef.current = recognition;
      setListening(true);
    } catch {
      setVoiceError(t('chat.voice.startError') || 'Could not start voice capture.');
    }
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch {
      // ignore
    }
    setListening(false);
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;
    // Light client-side validation — server will re-check.
    if (!fields.pickup_address || !fields.dropoff_address ||
        !fields.transport_date || !fields.horse_count) {
      setSubmitError(t('chat.form.missing') || 'Please fill in pickup, drop-off, date and horse count.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const { data } = await axios.post(`${API}/trip-requests`, {
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        contact_number: customer.phone || customer.contact_number || '',
        country: 'Morocco',
        horse_count: String(fields.horse_count),
        pickup_address: fields.pickup_address,
        dropoff_address: fields.dropoff_address,
        transport_date: fields.transport_date,
        additional_comments: fields.additional_comments || '',
      });
      setSuccess({ request_id: data.request_id });
      if (onCreated) onCreated(data.request_id);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || (t('chat.form.submitError') || 'Could not submit your request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flex-1 overflow-y-auto px-5 py-8 text-center bg-gradient-to-b from-emerald-900/20 to-[#0a0a0a]" data-testid="trip-form-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <h3 className="text-white font-bold text-lg mb-1">
          {t('chat.form.successTitle') || 'Request submitted!'}
        </h3>
        <p className="text-gray-400 text-sm mb-1">
          {t('chat.form.successBody') || 'Verified transporters will start sending quotes shortly.'}
        </p>
        <p className="text-emerald-400 text-xs font-mono mb-5">{success.request_id}</p>
        <button
          onClick={() => onCreated && onCreated(success.request_id, true)}
          data-testid="trip-form-view-bookings-btn"
          className="bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-full px-5 py-2.5 text-sm transition-colors"
        >
          {t('chat.cta.viewBookings') || 'View my bookings →'}
        </button>
      </div>
    );
  }

  if (step === 'voice-intro') {
    return (
      <div
        className="flex-1 overflow-y-auto px-5 py-8 bg-gradient-to-b from-black/40 to-[#0a0a0a] flex flex-col items-center text-center"
        data-testid="trip-form-voice-intro"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mb-4">
          <Truck className="w-6 h-6 text-black" />
        </div>
        <h3 className="text-white font-bold text-lg leading-tight mb-2">
          {t('chat.form.voiceTitle') || 'Just tell me about your trip'}
        </h3>
        <p className="text-gray-400 text-sm mb-1 max-w-xs">
          {t('chat.form.voiceBody') || "Tap the mic and say it naturally — I'll fill out the form for you."}
        </p>
        <p className="text-gray-500 text-xs italic mt-3 mb-6 max-w-xs">
          {t('chat.form.voiceExample') || 'e.g. "2 horses from Casablanca to Marrakech on August 25 at 9am"'}
        </p>

        <button
          type="button"
          onClick={listening ? stopListening : startListening}
          disabled={parsing}
          data-testid="trip-form-voice-intro-mic"
          className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all ${
            listening
              ? 'bg-red-500 scale-110 shadow-2xl shadow-red-500/40'
              : parsing
                ? 'bg-amber-500/50 cursor-wait'
                : 'bg-gradient-to-br from-amber-400 to-amber-600 hover:scale-105 shadow-2xl shadow-amber-500/30'
          }`}
          aria-label={listening ? 'Stop recording' : 'Start recording'}
        >
          {listening && (
            <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-50" />
          )}
          {listening
            ? <MicOff className="w-8 h-8 text-white" />
            : <Mic className="w-8 h-8 text-black" />}
        </button>
        <p className="text-xs mt-3 font-semibold min-h-[1.25rem]">
          {listening && <span className="text-red-400">{t('chat.voice.listeningShort') || 'Listening… tap to stop'}</span>}
          {parsing && <span className="text-amber-400 animate-pulse">{t('chat.voice.aiParsing') || 'AI is filling the form…'}</span>}
          {!listening && !parsing && <span className="text-gray-500">{t('chat.form.tapToStart') || 'Tap to start'}</span>}
        </p>

        {voiceTranscript && (
          <p className="mt-3 text-xs text-gray-300 italic px-3 py-2 bg-white/5 rounded-lg border border-white/10 max-w-xs" data-testid="trip-form-transcript">
            {`"${voiceTranscript}"`}
          </p>
        )}
        {voiceError && (
          <p className="mt-3 text-xs text-red-300 flex items-start gap-1.5 px-3 max-w-xs">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span className="text-left">{voiceError}</span>
          </p>
        )}

        <button
          type="button"
          onClick={() => setStep('form')}
          className="mt-6 text-gray-400 hover:text-amber-400 text-xs underline transition-colors"
          data-testid="trip-form-type-instead-btn"
        >
          {t('chat.form.typeInstead') || 'I prefer to type'}
        </button>
      </div>
    );
  }


  return (
    <div
      className="flex-1 overflow-y-auto px-4 py-5 bg-gradient-to-b from-black/40 to-[#0a0a0a]"
      data-testid="trip-form-in-bot"
    >
      <div className="mb-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
          <Truck className="w-4 h-4 text-black" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-bold text-base leading-tight">
            {t('chat.form.title') || 'New transport request'}
          </h3>
          <p className="text-gray-400 text-xs mt-0.5">
            {voiceSupported
              ? (t('chat.form.reviewHint') || 'Review the details below, then submit.')
              : (t('chat.form.subtitle') || 'Tell us about the trip and we\'ll forward it to transporters.')}
          </p>
        </div>
        {voiceSupported && (
          <button
            type="button"
            onClick={() => { setVoiceTranscript(''); setVoiceError(''); setStep('voice-intro'); }}
            data-testid="trip-form-rerecord-btn"
            className="text-amber-400 hover:text-amber-300 text-xs font-semibold inline-flex items-center gap-1 transition-colors"
          >
            <Mic className="w-3 h-3" />
            {t('chat.form.rerecord') || 'Re-record'}
          </button>
        )}
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="trip-form-fields">
        <div>
          <label className="block text-gray-300 text-xs font-medium mb-1">
            {t('chat.form.pickup') || 'Pick-up address'}
          </label>
          <AddressAutocomplete
            id="bot-pickup"
            value={fields.pickup_address}
            onChange={(v) => updateField('pickup_address', v)}
            onSelect={({ address }) => updateField('pickup_address', address)}
            placeholder={t('chat.form.pickupPh') || 'e.g. Casablanca, Hippodrome'}
            country="ma"
            testIdPrefix="trip-form-pickup"
          />
        </div>
        <div>
          <label className="block text-gray-300 text-xs font-medium mb-1">
            {t('chat.form.dropoff') || 'Drop-off address'}
          </label>
          <AddressAutocomplete
            id="bot-dropoff"
            value={fields.dropoff_address}
            onChange={(v) => updateField('dropoff_address', v)}
            onSelect={({ address }) => updateField('dropoff_address', address)}
            placeholder={t('chat.form.dropoffPh') || 'e.g. Marrakech equestrian club'}
            country="ma"
            testIdPrefix="trip-form-dropoff"
          />
        </div>
        <div>
          <label className="block text-gray-300 text-xs font-medium mb-1">
            {t('chat.form.date') || 'Transport date'}
          </label>
          {/* Override the inner `sm:grid-cols-2` to keep date + time stacked
              inside the narrow chat panel — otherwise the buttons get cramped
              to ~140px each at the sm breakpoint. */}
          <div className="[&>div]:!grid-cols-1">
            <DateTimePickerField
              value={fields.transport_date}
              onChange={(v) => updateField('transport_date', v)}
            />
          </div>
        </div>
        <div>
          <label className="block text-gray-300 text-xs font-medium mb-1">
            {t('chat.form.horses') || 'Horses'}
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={fields.horse_count}
            onChange={(e) => updateField('horse_count', e.target.value)}
            placeholder="1"
            data-testid="trip-form-horses-input"
            className="w-full bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none rounded-lg px-3 py-2 text-white text-sm placeholder:text-gray-500"
          />
        </div>
        <div>
          <label className="block text-gray-300 text-xs font-medium mb-1">
            {t('chat.form.notes') || 'Additional notes (optional)'}
          </label>
          <textarea
            value={fields.additional_comments}
            onChange={(e) => updateField('additional_comments', e.target.value)}
            rows={2}
            placeholder={t('chat.form.notesPh') || 'Anything transporters should know?'}
            data-testid="trip-form-notes-input"
            className="w-full bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none rounded-lg px-3 py-2 text-white text-sm placeholder:text-gray-500 resize-none"
          />
        </div>

        {submitError && (
          <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs" data-testid="trip-form-error">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="trip-form-submit-btn"
          className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold rounded-full py-2.5 text-sm transition-colors"
        >
          {submitting
            ? (t('chat.form.submitting') || 'Submitting…')
            : (t('chat.form.submit') || 'Get quotes from transporters')}
        </button>
      </form>
    </div>
  );
};

export default TripFormInBot;
